import queryString from "query-string";

import Context from "shared/util/Context";
import LocalEnvironment from "shared/util/LocalEnvironment";
import isRouteMatch from "shared/util/isRouteMatch";
import { isDesktop, isDev } from "shared/util/EnvironmentHelpers";
import { ROUTES } from "routers/shared/util/consts";
import OneTrust from "shared/util/oneTrust";

import {
	PREBID_VERSION,
	FORMATS,
	FORMATS_AOL,
	FORMATS_INDEX_EXCHANGE,
	PLACEMENTS_CRITEO,
	PLACEMENTS_OPENX,
	PLACEMENTS_AOL,
	PLACEMENTS_APPNEXUS,
	PLACEMENTS_INDEX_EXCHANGE,
	PLACEMENTS_RUBICON,
	PAGEMAP,
} from "./constants.js";

export function isDebugMode() {
	const queryObject = queryString.parse(window.location.search);
	const isDebug = "prebidDebug" in queryObject;

	return isDev || isDebug;
}

// external script loaders
// -----------------------------------------------------------------------------

export function loadGPT() {
	return OneTrust.loadScript(
		"https://www.googletagservices.com/tag/js/gpt.js",
		OneTrust.CATEGORY.ADVERTISING,
	);
}

export function loadPBJS() {
	const cdn = Context.get("cdnJS");
	const src = isDebugMode() ?
		`${LocalEnvironment.getEnvironmentUrl()}/flat/js/lib/prebid-${PREBID_VERSION}.js` :
		`${cdn}/flat/js/lib/prebid-${PREBID_VERSION}.min.js`;

	return OneTrust.loadScript(src, OneTrust.CATEGORY.ADVERTISING);
}

// setup networks
// -----------------------------------------------------------------------------

export function setupPBJS() {
	window.pbjs.que.push(() => {
		window.pbjs.setConfig({
			ix: {
				timeout: Context.get("prebidTimeout"),
			},
			enableSendAllBids: true,
		});

		window.pbjs.bidderSettings = {
			standard: {
				adserverTargeting: [
					{
						key: "hb_bidder",

						val(bidResponse) {
							return bidResponse.bidderCode;
						},
					},
					{
						key: "hb_adid",

						val(bidResponse) {
							return bidResponse.adId;
						},
					},
					{
						key: "hb_pb",

						val(bidResponse) {
							const { cpm } = bidResponse;
							let priceBucket;

							if (cpm < 5.00) {
								priceBucket = (Math.floor(cpm * 20) / 20).toFixed(2);
							} else if (cpm < 10.00) {
								priceBucket = (Math.floor(cpm * 10) / 10).toFixed(2);
							} else if (cpm < 20.00) {
								priceBucket = (Math.floor(cpm * 2) / 2).toFixed(2);
							} else {
								priceBucket = "20.00";
							}

							return priceBucket;
						},
					},
				],
			},
		};
	});
}

export function setupGPT() {
	window.googletag.cmd.push(() => {
		const ref = (document.referrer.indexOf("okcupid.com") !== -1) ?
			document.referrer : "//www.okcupid.com/home";

		window.googletag.pubads().set("page_url", ref);
		window.googletag.pubads().enableSingleRequest();
		window.googletag.pubads().enableAsyncRendering();
		window.googletag.enableServices();
	});
}


// get ad units
// -----------------------------------------------------------------------------

export function getSlotName(location = window.location) {
	const pageSlotMap = {
		"/home": "Home",
		"/doubletake": "Home",
		"/stacks": "Home",
		"/logout": "Logout",
		"/mailbox": "Mailbox",
		"/messages": "Mailbox",
		"/match": "Match",
		"/help/a-list-extras": "Help-A-List-",
		"/feedback": "Feedback",
		"/who-you-like": "Who-I-Rated",
		"/who-likes-you": "Who-I-Rated",
		"/settings": "Settings",
		"/discovery": "Discovery",
	};

	let slotName = "";

	if (pageSlotMap[location.pathname]) {
		slotName = pageSlotMap[location.pathname];
	} else if (isRouteMatch(location, ROUTES.ProfileQuestions)) {
		slotName = "Profile-Questions";
	} else if (isRouteMatch(location, ROUTES.ProfilePhotos)) {
		slotName = "Profile-Photos";
	} else if (isRouteMatch(location, ROUTES.ProfileMain)) {
		slotName = "Profile";
	}

	return slotName;
}

function _getPage() {
	return PAGEMAP[getSlotName()];
}

/**
 * Get the id of an ad placement. This ID is sometimes called `placement` or
 * `zoneId` depending on the ad network.
 * @param {Object} placements - a dictionary of pages to placement ids
 * @param {string} format - the name of the ad that you're trying to fill
 * @return {number} [placementId] - the placement id found, or null
 */
function _getPlacementId(placements, format) {
	const page = _getPage();
	let placementId;

	if (placements[format]) {
		placementId = placements[format];
	} else if (page && placements[page] && placements[page][format]) {
		placementId = placements[page][format];
	}

	return placementId;
}

function _addAOL(bids, format) {
	const aolFormat = FORMATS_AOL[format];
	const placementId = _getPlacementId(PLACEMENTS_AOL, format);

	if (!placementId || !aolFormat) {
		return bids;
	}

	return bids.concat([{
		bidder: "aol",
		params: {
			placement: placementId,
			network: "9457.1",
			sizeId: aolFormat,
		},
	}]);
}

const _indexExchangePlacements = {};

function _addIndexExchange(bids, format, sizes) {
	const iePlacement = PLACEMENTS_INDEX_EXCHANGE[format];
	const ieFormat = FORMATS_INDEX_EXCHANGE[format];

	_indexExchangePlacements[format] = (_indexExchangePlacements[format] || 0) + 1;

	if (iePlacement && ieFormat) {
		sizes.forEach((size) => {
			bids.push({
				bidder: "ix",
				params: {
					id: ieFormat + _indexExchangePlacements[format],
					siteId: iePlacement,
					size,
				},
			});
		});

		return bids;
	} else {
		return bids;
	}
}

function _addAppNexus(bids, format) {
	const page = _getPage();
	const placementId = _getPlacementId(PLACEMENTS_APPNEXUS, format);

	if (!placementId) {
		return bids;
	}

	return bids.concat([{
		bidder: "appnexus",
		params: {
			placementId,
			referrer: "okcupid.com",
		},
	}]);
}

function _addOpenX(bids, format) {
	const country = Context.get("country");
	const code = PLACEMENTS_OPENX[country] ?
		PLACEMENTS_OPENX[country][format] :
		PLACEMENTS_OPENX.OTHER[format];

	if (!Context.get("openXBidding") || !code) {
		return bids;
	}

	return bids.concat([{
		bidder: "openx",
		params: {
			unit: code,
			delDomain: "match-d.openx.net",
		},
	}]);
}

function _addCriteo(bids, format) {
	if (!Context.get("criteoBidding") || Context.get("country") !== "US") {
		return bids;
	}

	const code = PLACEMENTS_CRITEO[format];

	if (!code) {
		return bids;
	}

	return bids.concat([{
		bidder: "criteo",
		params: {
			zoneId: code,
		},
	}]);
}

function _addRubicon(bids, format) {
	const placementId = _getPlacementId(PLACEMENTS_RUBICON, format);

	if (!placementId) {
		return bids;
	}

	return bids.concat([{
		bidder: "rubicon",
		params: {
			accountId: "4612",
			siteId: "262912",
			zoneId: placementId,
		},
	}]);
}

export function getAdUnits(units) {
	const page = getSlotName();
	let slot;

	if (isDesktop) {
		slot = `/7214/mat.okcupid/${page}`;
	} else {
		const gender = Context.get("gender");
		const genderName = gender === 1 ? "m" : "f";
		slot = `/7214/OKCupid_Mobile/${genderName}`;
	}

	return units
		.filter(({ id, format }) => id && FORMATS[format])
		.map(({ id, format }) => {
			let sizes = FORMATS[format];
			const p = page;

			// more sizes on home
			if (p === "Home" && format === "leader") {
				sizes = FORMATS.leader_expanded;
			}

			let bids = [];

			bids = _addAppNexus(bids, format);
			bids = _addAOL(bids, format);
			bids = _addIndexExchange(bids, format, sizes);
			bids = _addCriteo(bids, format);
			bids = _addOpenX(bids, format);

			if (Context.get("country") === "US") {
				bids = _addRubicon(bids, format);
			}

			// return the appnexus bundle
			const bundle = {
				bids,
				slot,
				sizes,
				format,
				code: id,
			};

			return bundle;
		});
}

export function formatAdUnitsForPBJS(units) {
	if (!units || !units.length) {
		return units;
	}

	return units.map(({ sizes, ...ad }) => {
		return {
			mediaTypes: {
				banner: {
					sizes,
				},
			},
			...ad,
		};
	});
}



// WEBPACK FOOTER //
// ./src/shared/modules/onpageads/functions.js